/**
 * Time Calculation Service
 * 
 * Provides standardized time calculations for Cursor AI time, AI time, and NonAI time
 * based on the backend API response structure.
 */

export interface GroupItem {
  applicationName: string;
  applicationId: number;
  categoryColor: string;
  activityLevel: string;
  spentTime: number;
}

export interface AdvancedGroup {
  sectionName: string;
  color: string;
  groupItems: GroupItem[];
  spentTime: number;
}

export interface UserTimeData {
  grouping: {
    periodLong: number;
    totalTrackedTime: number;
    alignmentScore: number;
    advancedGroups: AdvancedGroup[];
  };
}

export interface TimeStats {
  cursorTime: number;
  aiTime: number;
  nonAiTime: number;
  totalTime: number;
}

/**
 * Calculate all time statistics in a single iteration for optimal performance
 * Returns an object with cursorTime, aiTime, and nonAiTime
 */
export function calculateAllTimeStats(advancedGroups: AdvancedGroup[]): { cursorTime: number; aiTime: number; nonAiTime: number } {
  let cursorTime = 0;
  let aiTime = 0;
  let nonAiTime = 0;
  
  for (const section of advancedGroups) {
    if (section.sectionName === 'AI') {
      // For AI sections, iterate through applications
      for (const application of section.groupItems || []) {
        if (application.applicationName === 'cursor') {
          cursorTime += application.spentTime || 0;
        } else {
          aiTime += application.spentTime || 0;
        }
      }
    } else if (section.sectionName === 'PURE_AI') {
      // For PURE_AI sections, use section-level time
      aiTime += section.spentTime || 0;
    } else {
      // For all other sections, add to nonAiTime
      nonAiTime += section.spentTime || 0;
    }
  }
  
  return { cursorTime, aiTime, nonAiTime };
}

/**
 * Calculate time spent specifically in Cursor AI
 * Only counts Cursor time when it appears in the "AI" section
 * @deprecated Use calculateAllTimeStats for better performance when calculating multiple values
 */
export function calculateCursorTime(advancedGroups: AdvancedGroup[]): number {
  return calculateAllTimeStats(advancedGroups).cursorTime;
}

/**
 * Calculate total AI time excluding Cursor
 * Includes:
 * - All applications in "AI" sections except Cursor (application-level time)
 * - All time from "PURE_AI" sections (section-level time)
 * @deprecated Use calculateAllTimeStats for better performance when calculating multiple values
 */
export function calculateAiTime(advancedGroups: AdvancedGroup[]): number {
  return calculateAllTimeStats(advancedGroups).aiTime;
}

/**
 * Calculate NonAI time
 * Includes all time from sections that are not "AI" or "PURE_AI"
 * Uses section-level spentTime
 * @deprecated Use calculateAllTimeStats for better performance when calculating multiple values
 */
export function calculateNonAiTime(advancedGroups: AdvancedGroup[]): number {
  return calculateAllTimeStats(advancedGroups).nonAiTime;
}

/**
 * Calculate all time statistics for a user
 * Returns Cursor time, AI time, NonAI time, and total time
 * Uses optimized single-iteration calculation for better performance
 */
export function calculateTimeStats(userData: UserTimeData): TimeStats {
  const advancedGroups = userData?.grouping?.advancedGroups || [];
  
  // Use the optimized single-iteration calculation
  const { cursorTime, aiTime, nonAiTime } = calculateAllTimeStats(advancedGroups);
  const totalTime = cursorTime + aiTime + nonAiTime;
  
  return {
    cursorTime,
    aiTime,
    nonAiTime,
    totalTime
  };
}

/**
 * Calculate time statistics for multiple users
 * Applies the same calculations to an array of user data
 */
export function calculateTimeStatsForUsers(users: UserTimeData[]): TimeStats[] {
  return users.map(userData => calculateTimeStats(userData));
}

/**
 * Utility function to validate time data
 * Returns true if the data structure is valid for calculations
 */
export function validateTimeData(userData: UserTimeData): boolean {
  if (!userData?.grouping?.advancedGroups) {
    return false;
  }
  
  // Check if advancedGroups is an array
  if (!Array.isArray(userData.grouping.advancedGroups)) {
    return false;
  }
  
  // Validate each section has required properties
  for (const section of userData.grouping.advancedGroups) {
    if (typeof section.sectionName !== 'string') {
      return false;
    }
    
    if (typeof section.spentTime !== 'number' || section.spentTime < 0) {
      return false;
    }
    
    if (section.groupItems && !Array.isArray(section.groupItems)) {
      return false;
    }
    
    // Validate group items if they exist
    if (section.groupItems) {
      for (const item of section.groupItems) {
        if (typeof item.applicationName !== 'string') {
          return false;
        }
        
        if (typeof item.spentTime !== 'number' || item.spentTime < 0) {
          return false;
        }
      }
    }
  }
  
  return true;
}

/**
 * Safe calculation wrapper that handles invalid data
 * Returns zero stats if data is invalid
 * Uses optimized single-iteration calculation for better performance
 */
export function safeCalculateTimeStats(userData: UserTimeData): TimeStats {
  if (!validateTimeData(userData)) {
    return {
      cursorTime: 0,
      aiTime: 0,
      nonAiTime: 0,
      totalTime: 0
    };
  }
  
  return calculateTimeStats(userData);
}

/**
 * Aggregate time stats from multiple users or weeks
 * Useful for rolling period calculations and team summaries
 */
export function aggregateTimeStats(timeStatsArray: TimeStats[]): TimeStats {
  return timeStatsArray.reduce(
    (total, stats) => ({
      cursorTime: total.cursorTime + stats.cursorTime,
      aiTime: total.aiTime + stats.aiTime,
      nonAiTime: total.nonAiTime + stats.nonAiTime,
      totalTime: total.totalTime + stats.totalTime
    }),
    { cursorTime: 0, aiTime: 0, nonAiTime: 0, totalTime: 0 }
  );
}

/**
 * Calculate time stats for a rolling period (multiple weeks)
 * Properly aggregates time across weeks for the same user
 */
export function calculateTimeStatsForPeriod(weeklyActivities: UserTimeData[]): TimeStats {
  const weeklyStats = weeklyActivities.map(activity => calculateTimeStats(activity));
  return aggregateTimeStats(weeklyStats);
}

/**
 * Validate that calculated time stats match expected total
 * Useful for data integrity checks
 */
export function validateTimeConsistency(calculated: TimeStats, expectedTotal: number): boolean {
  const tolerance = 1; // Allow 1 minute tolerance for rounding
  const actualTotal = calculated.cursorTime + calculated.aiTime + calculated.nonAiTime;
  const difference = Math.abs(actualTotal - expectedTotal);
  
  if (difference > tolerance) {
    console.warn('⚠️ Time calculation mismatch:', {
      calculated: actualTotal,
      expected: expectedTotal,
      difference,
      breakdown: calculated
    });
    return false;
  }
  
  return true;
}

/**
 * Create empty time stats (useful for initializing)
 */
export function createEmptyTimeStats(): TimeStats {
  return {
    cursorTime: 0,
    aiTime: 0,
    nonAiTime: 0,
    totalTime: 0
  };
}